import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { PROFILE } from "../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Your message is on its way. Thank you!");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-[#2C223A] focus:border-[#D6B56D] text-[#EBE4D8] placeholder:text-[#A59DAB]/60 py-3 outline-none transition-colors duration-300";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative z-10 py-28 md:py-36 px-6"
    >
      <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, x: -24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
        >
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D6B56D] mb-6">
            Connect
          </p>
          <h2 className="font-heading font-light text-5xl md:text-6xl text-[#EBE4D8] leading-tight mb-6">
            Let's get <span className="italic text-[#D6B56D]">in touch</span>
          </h2>
          <p className="text-[#A59DAB] leading-relaxed font-light mb-8 max-w-sm">
            Whether you want to share a story, plan an adventure, or just say
            hello — the door is always open.
          </p>
          <div className="space-y-2 text-[#A59DAB]">
            <p data-testid="contact-email">
              <span className="text-[#D6B56D]">Email · </span>
              {PROFILE.email}
            </p>
            <p>
              <span className="text-[#D6B56D]">Now · </span>
              {PROFILE.location}
            </p>
          </div>
        </motion.div>

        <motion.form
          data-testid="contact-form"
          onSubmit={submit}
          initial={{ opacity: 0, x: 24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="backdrop-blur-xl bg-[#120E1A]/60 border border-[#D6B56D]/20 rounded-3xl p-8 md:p-10 space-y-7"
        >
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[#A59DAB]">
              Name
            </label>
            <input
              data-testid="contact-name-input"
              value={form.name}
              onChange={update("name")}
              placeholder="What's your name?"
              className={inputCls}
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[#A59DAB]">
              Email
            </label>
            <input
              data-testid="contact-email-input"
              type="email"
              value={form.email}
              onChange={update("email")}
              placeholder="your@email.com"
              className={inputCls}
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[#A59DAB]">
              Message
            </label>
            <textarea
              data-testid="contact-message-input"
              value={form.message}
              onChange={update("message")}
              rows={3}
              placeholder="Write me your thought..."
              className={`${inputCls} resize-none`}
            />
          </div>
          <button
            data-testid="contact-submit-button"
            type="submit"
            disabled={loading}
            className="w-full px-8 py-3.5 rounded-full bg-[#D6B56D] text-[#06040A] text-sm tracking-widest uppercase font-medium hover:bg-[#F0D494] transition-all duration-300 disabled:opacity-60"
          >
            {loading ? "Sending..." : "Send message"}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
